'use client';

import { getFragmentData } from '@/__generated__';
import { ENTRY_BASE_FRAGMENT } from '@/gql/fragments/entries.gql';
import { createToggleStore } from '@/lib/utils/createToggleStore';
import useScrollPosition from '@/lib/utils/useScrollPosition';
import lottie from 'lottie-web';
import { useEffect, useRef } from 'react';
import { useNavBar } from '../NavBar/useNavBar';
import NextAnchor from '../ui/NextAnchor';
import * as styles from './Animation.css';
import LogoDarkHover from './LogoDarkHover.json';
import LogoDarkIntro from './LogoDarkIntro.json';
import LogoLightHover from './LogoLightHover.json';
import LogoLightIntro from './LogoLightIntro.json';

const useAnimatedStore = createToggleStore({
  defaultValue: false,
  allowNull: false,
});

export default function Animation() {
  const { value: animatedValue, toggle: toggleAnimatedValue } = useAnimatedStore();
  const { page } = useNavBar();
  const pos = useScrollPosition();

  const base = getFragmentData(ENTRY_BASE_FRAGMENT, page);
  const isEvent = base?.sectionHandle === 'event';

  const isEventisTop = pos < 10 && isEvent;

  const introRef = useRef<HTMLDivElement>(null);
  const hoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!introRef.current || !hoverRef.current) return;

    const lottieHover = lottie.loadAnimation({
      animationData: isEventisTop ? LogoLightHover : LogoDarkHover,
      autoplay: false,
      container: hoverRef.current,
      loop: false,
      name: 'logo-hover-animation',
      renderer: 'svg',
    });

    if (animatedValue) {
      // Show hover animation immediately if intro already played
      hoverRef.current.style.display = 'block';
      return () => {
        lottieHover.destroy();
      };
    }

    // Hide hover animation initially
    hoverRef.current.style.display = 'none';

    const lottieIntro = lottie.loadAnimation({
      animationData: isEventisTop ? LogoLightIntro : LogoDarkIntro,
      autoplay: true,
      container: introRef.current,
      loop: false,
      name: 'logo-intro-animation',
      renderer: 'svg',
    });

    lottieIntro.addEventListener('complete', () => {
      lottieIntro.destroy();
      if (hoverRef.current) {
        hoverRef.current.style.display = 'block';
      }
      toggleAnimatedValue(true);
    });

    return () => {
      lottieIntro.destroy();
      lottieHover.destroy();
    };
  }, [isEventisTop, animatedValue, toggleAnimatedValue]);

  // Controls the hover animation
  useEffect(() => {
    const hoverEl = hoverRef.current;
    if (!hoverEl) return;

    const playHover = () => {
      lottie.play('logo-hover-animation');
    };

    const stopHover = () => {
      lottie.stop('logo-hover-animation');
    };

    hoverEl.addEventListener('mouseenter', playHover);
    hoverEl.addEventListener('mouseleave', stopHover);

    return () => {
      hoverEl.removeEventListener('mouseenter', playHover);
      hoverEl.removeEventListener('mouseleave', stopHover);
    };
  }, []);

  return (
    <NextAnchor
      href="/"
      aria-label="logo"
      className={styles.root({ isEvent: isEventisTop && !isEvent })}>
      <div ref={introRef} />
      <div ref={hoverRef} />
    </NextAnchor>
  );
}
